import type { IcmPeriod } from "@/icm/types";
import type { IPayrollRun } from "@/payroll/payroll.d";

export type PayBreakdownPeriod = IcmPeriod;

export interface PayPlanResponse {
  plan_document: string | null;
  ai_script_version_id: string;
  ai_script_version_number: number;
}

export const PAY_BREAKDOWN_RUN_TARGET_TYPE = {
  aiScript: "ai-script",
  icm: "icm",
  legacyScript: "legacy-script",
  none: "none",
} as const;

export const PAY_BREAKDOWN_CALCULATION_STATE = {
  idle: "idle",
  initial: "initial",
  rerun: "rerun",
  background: "background",
} as const;

export type PayBreakdownCalculationState =
  (typeof PAY_BREAKDOWN_CALCULATION_STATE)[keyof typeof PAY_BREAKDOWN_CALCULATION_STATE];

// Top-level section sub-tabs for a pay period. `breakdown` is the Grid /
// output (default); `roster` is the read-only roster the run used; `payPlan` is
// the versioned pay plan the run used; `packs` is the statement delivery surface;
// `packChat` is what technicians said back about those packs; `adjustments` is the
// ServiceTitan payroll-adjustment push and its audit trail.
// An `edits` section is planned but not built.
export const PAY_BREAKDOWN_SECTION = {
  breakdown: "grid",
  roster: "roster",
  payPlan: "pay-plan",
  packs: "packs",
  packChat: "pack-chat",
  adjustments: "adjustments",
} as const;

export type PayBreakdownSection =
  (typeof PAY_BREAKDOWN_SECTION)[keyof typeof PAY_BREAKDOWN_SECTION];

export const PAYROLL_EMAILS_ACCESS = {
  hidden: "hidden",
  preview: "preview",
  enabled: "enabled",
} as const;

export type PayrollEmailsAccess =
  (typeof PAYROLL_EMAILS_ACCESS)[keyof typeof PAYROLL_EMAILS_ACCESS];

export const AI_SCRIPT_RERUN_VERSION_CHOICE = {
  keep: "keep",
  latest: "latest",
} as const;

export type AiScriptRerunVersionChoice =
  (typeof AI_SCRIPT_RERUN_VERSION_CHOICE)[keyof typeof AI_SCRIPT_RERUN_VERSION_CHOICE];

export interface AiScriptRerunRunOptions {
  aiScriptVersionId?: string;
  payrollRosterId?: string;
}

export interface AiScriptRerunVersionInfo {
  runScriptVersion: number | null;
  runRosterVersion: number | null;
  currentScriptVersion: number | null;
  currentRosterVersion: number | null;
  runScriptVersionId: string | null;
  runRosterId: string | null;
}

export interface AiScriptRerunSelection {
  versionInfo: AiScriptRerunVersionInfo;
  scriptChoice: AiScriptRerunVersionChoice | null;
  rosterChoice: AiScriptRerunVersionChoice | null;
  isScopedRerun: boolean;
}

export interface AiScriptRerunState {
  scriptDrift: boolean;
  rosterDrift: boolean;
  hasDrift: boolean;
  scriptVersion: number | null;
  rosterVersion: number | null;
}

export interface PayBreakdownVersionContext {
  rosterVersionNumber: number | null;
  currentRosterVersionNumber: number | null;
  rerunVersionInfo: AiScriptRerunVersionInfo;
}

export type PayBreakdownOutput =
  | { type: typeof PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript; runId: string }
  | { type: typeof PAY_BREAKDOWN_RUN_TARGET_TYPE.icm; runId: string }
  | {
      type: typeof PAY_BREAKDOWN_RUN_TARGET_TYPE.legacyScript;
      run: IPayrollRun;
    }
  | { type: typeof PAY_BREAKDOWN_RUN_TARGET_TYPE.none };

export type PayBreakdownRunTarget =
  | {
      type: typeof PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript;
      canRunPeriod: boolean;
      disabledReason?: string;
      /** Whether this user has any whole-workbook or scoped route to a rerun. */
      canAccessRerun: boolean;
      /**
       * Whether the user may rerun the WHOLE payroll. False for a department lead
       * assigned a single sheet. The shared rerun control limits them to their
       * own section.
       */
      canRunWholePayroll: boolean;
    }
  | {
      type: typeof PAY_BREAKDOWN_RUN_TARGET_TYPE.icm;
      canRunPeriod: boolean;
      disabledReason?: string;
    }
  | {
      type: typeof PAY_BREAKDOWN_RUN_TARGET_TYPE.legacyScript;
      canRunPeriod: boolean;
      disabledReason?: string;
    }
  | {
      type: typeof PAY_BREAKDOWN_RUN_TARGET_TYPE.none;
      canRunPeriod: false;
      disabledReason?: string;
    };

export interface PayBreakdownRerunAction {
  isVisible: boolean;
  onRun: () => void;
  canRunPeriod: boolean;
  isRunning: boolean;
  disabledReason: string | undefined;
}

export interface PayBreakdownDownloadAction {
  isDownloading: boolean;
  onDownload: () => void;
}

export interface PayBreakdownManualEditsAction {
  reviewCount: number;
  onOpen: () => void;
}

export interface PayBreakdownCommentsAction {
  openCount: number;
  onOpen: () => void;
}
